import React, { useState } from "react";
import { Button, Checkbox, Input, Modal, notification, Radio } from "antd";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { Asset, ILocalAsset } from "../../types";
import {
  useDeleteAssetMutation,
  useUnmatchAssetMutation,
} from "../../api/graphql/mutations";
import { AssetDeletionReason } from "../../api/graphql/schema.generated";

const UNMATCH_REASON_KEYS = [
  "WRONG_AMOUNT",
  "WRONG_INVOICE_NUMBER",
  "WRONG_DATE",
  "WRONG_NAME",
] as const;

const DELETE_REASON_KEYS = [
  AssetDeletionReason.DUPLICATE,
  AssetDeletionReason.WRONG_RECEIPT,
  AssetDeletionReason.NOT_A_RECEIPT,
  AssetDeletionReason.UPLOADED_BY_MISTAKE,
  AssetDeletionReason.OTHER,
] as const;

const Wrapper = styled.span<{ $sticky?: boolean }>`
  z-index: 1;
  top: 10px;
  left: 10px;
  width: 0;
  height: fit-content;
  order: -1;
  position: ${({ $sticky }) => ($sticky ? "sticky" : "absolute")};
  align-self: flex-start;

  button {
    white-space: nowrap;
  }
`;

const GroupTitle = styled.div`
  margin: 16px 0 8px;
  font-weight: 600;
`;

interface RemoveReceiptButtonProps {
  asset: Asset;
  sticky?: boolean;
  onConfirm?: (
    asset: Asset | ILocalAsset,
    deletedRemoteAsset?: boolean
  ) => void;
}

const RemoveReceiptButton: React.FC<RemoveReceiptButtonProps> = ({
  asset,
  sticky,
  onConfirm,
}) => {
  const { t } = useTranslation([
    "RemoveReceiptModal",
    "DeleteEmailDocumentModal",
  ]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [unmatchReasons, setUnmatchReasons] = useState<string[]>([]);
  const [deletionReason, setDeletionReason] = useState<AssetDeletionReason>();
  const [deletionReasonText, setDeletionReasonText] = useState("");
  const [deleteAsset] = useDeleteAssetMutation();
  const [unmatchAsset] = useUnmatchAssetMutation();

  const reset = () => {
    setUnmatchReasons([]);
    setDeletionReason(undefined);
    setDeletionReasonText("");
  };

  const close = () => {
    reset();
    setIsOpen(false);
  };

  const canConfirm =
    (!!deletionReason &&
      (deletionReason !== "OTHER" || deletionReasonText.trim().length > 0)) ||
    unmatchReasons.length > 0;

  const handleConfirm = async () => {
    if (!asset.id || !canConfirm) return;

    setIsLoading(true);
    try {
      let success = false;
      if (deletionReason) {
        const result = await deleteAsset({
          variables: {
            id: asset.id,
            deletionReason,
            deletionReasonText:
              deletionReason === "OTHER"
                ? deletionReasonText.trim()
                : undefined,
          },
        });
        success = result.data?.deleteAsset?.success ?? false;
      } else {
        const result = await unmatchAsset({
          variables: { id: asset.id, unmatchReasons },
        });
        success = result.data?.unmatchAsset?.success ?? false;
      }

      if (!success) {
        throw new Error("Receipt removal returned success: false");
      }

      notification.success({
        message: t(deletionReason ? "successDeleted" : "successUnmatched"),
      });
      onConfirm?.(asset, !!deletionReason);
      close();
    } catch (error) {
      notification.error({
        message: t("error", { message: (error as Error).message }),
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Wrapper $sticky={sticky}>
      <Button danger onClick={() => setIsOpen(true)}>
        {t("button")}
      </Button>
      <Modal
        open={isOpen}
        title={t("title")}
        onCancel={close}
        footer={[
          <Button key="cancel" onClick={close} disabled={isLoading}>
            {t("cancel")}
          </Button>,
          <Button
            key="confirm"
            danger
            type="primary"
            disabled={!canConfirm}
            loading={isLoading}
            onClick={handleConfirm}
          >
            {t("confirm")}
          </Button>,
        ]}
      >
        {asset.wasMatched !== false && (
          <>
            <GroupTitle>{t("unmatchGroup")}</GroupTitle>
            <Checkbox.Group
              value={unmatchReasons}
              onChange={(values) => {
                setUnmatchReasons(values as string[]);
                if (values.length) {
                  setDeletionReason(undefined);
                  setDeletionReasonText("");
                }
              }}
              style={{ display: "flex", flexDirection: "column", gap: 8 }}
              options={UNMATCH_REASON_KEYS.map((key) => ({
                value: key,
                label: t(`unmatchReasons.${key}`),
              }))}
            />
          </>
        )}

        <GroupTitle>{t("deleteGroup")}</GroupTitle>
        <Radio.Group
          value={deletionReason}
          onChange={(event) => {
            setDeletionReason(event.target.value);
            setUnmatchReasons([]);
          }}
          style={{ display: "flex", flexDirection: "column", gap: 8 }}
          options={DELETE_REASON_KEYS.map((key) => ({
            value: key,
            label: t(`reasons.${key}`, {
              ns: "DeleteEmailDocumentModal",
            }),
          }))}
        />
        {deletionReason === "OTHER" && (
          <Input.TextArea
            value={deletionReasonText}
            onChange={(event) => setDeletionReasonText(event.target.value)}
            placeholder={
              t("placeholder", {
                ns: "DeleteEmailDocumentModal",
              }) as string
            }
            rows={3}
            style={{ marginTop: 12 }}
          />
        )}
      </Modal>
    </Wrapper>
  );
};

export default RemoveReceiptButton;
